/**
 * What the frequency picker holds, and the one rule that keeps it honest: the
 * picker claims an expression only when it can hand that expression back
 * byte for byte.
 *
 * Each frequency's controls hold single numbers — a minute, an hour, a day of
 * the week, a day of the month, a month. An expression whose fields are exactly
 * those numbers, with every other field left as the wildcard, is claimed by the
 * frequency those controls belong to. Anything else — a step, a list, a range, a
 * name, a sixth field, a macro, other spacing — opens as a custom expression,
 * shown verbatim. Claiming it would put a value no control can display into the
 * controls, and the first edit would rewrite the schedule into one the picker
 * CAN display: every five minutes becoming once an hour.
 *
 * So `serializeCronPickerState(parseCronPickerState(expression)) === expression`
 * holds for every string, and a claimed state holds only values its controls
 * represent.
 *
 * It lives in `@nexus/types` because two sides judge it: the admin console's
 * picker opens stored schedules with it, and the backend's
 * `cron-default-schedules-open-honestly.spec.ts` holds every schedule the
 * product ships as a cron default to opening honestly here. The frontend's
 * `cron-expression/cron-picker-state.ts` re-exports it beside the per-frequency
 * drafts, which are the picker's own UI state.
 */

/** Which frequency the picker shows; `custom` holds any expression verbatim. */
export type FrequencyType = "custom" | "hourly" | "daily" | "weekly" | "monthly" | "yearly";

/** What the picker holds for each frequency; this module owns which expression opens as which. */
export type CronPickerState =
  | { readonly frequency: "hourly"; readonly minute: string }
  | { readonly frequency: "daily"; readonly minute: string; readonly hour: string }
  | {
      readonly frequency: "weekly";
      readonly minute: string;
      readonly hour: string;
      readonly dayOfWeek: string;
    }
  | {
      readonly frequency: "monthly";
      readonly minute: string;
      readonly hour: string;
      readonly day: string;
    }
  | {
      readonly frequency: "yearly";
      readonly minute: string;
      readonly hour: string;
      readonly day: string;
      readonly month: string;
    }
  | { readonly frequency: "custom"; readonly expression: string };

export type PickerStateOf<F extends FrequencyType> = Extract<CronPickerState, { frequency: F }>;

/** The wildcard: the only non-numeric value a picker field may hold. */
export const CRON_WILDCARD = "*";
const ANY = CRON_WILDCARD;

/** The inclusive range each control represents. */
const CONTROL_RANGES = {
  minute: { min: 0, max: 59 },
  hour: { min: 0, max: 23 },
  day: { min: 1, max: 31 },
  month: { min: 1, max: 12 },
  dayOfWeek: { min: 0, max: 6 }
} as const;

export type CronField = keyof typeof CONTROL_RANGES;

const CRON_FIELDS = [
  "minute",
  "hour",
  "day",
  "month",
  "dayOfWeek"
] as const satisfies readonly CronField[];

type CronFields = Readonly<Record<CronField, string>>;

type FixedFrequency = Exclude<FrequencyType, "custom">;

/**
 * The fields each fixed frequency's controls hold; every other field is the
 * wildcard. Decided by the FREQUENCY, never read off a candidate's values, or a
 * wildcard copied into a candidate's day would pass for "no day control".
 */
const CONTROLLED_FIELDS: { readonly [F in FixedFrequency]: readonly CronField[] } = {
  hourly: ["minute"],
  daily: ["minute", "hour"],
  weekly: ["minute", "hour", "dayOfWeek"],
  monthly: ["minute", "hour", "day"],
  yearly: ["minute", "hour", "day", "month"]
};

/** The five fields of a picker state, the wildcard in every field its controls do not hold. */
function fieldsOf(state: PickerStateOf<FixedFrequency>): CronFields {
  const wild: CronFields = { minute: ANY, hour: ANY, day: ANY, month: ANY, dayOfWeek: ANY };
  switch (state.frequency) {
    case "hourly":
      return { ...wild, minute: state.minute };
    case "daily":
      return { ...wild, minute: state.minute, hour: state.hour };
    case "weekly":
      return { ...wild, minute: state.minute, hour: state.hour, dayOfWeek: state.dayOfWeek };
    case "monthly":
      return { ...wild, minute: state.minute, hour: state.hour, day: state.day };
    case "yearly":
      return {
        ...wild,
        minute: state.minute,
        hour: state.hour,
        day: state.day,
        month: state.month
      };
  }
}

/** The expression a picker state stands for. */
export function serializeCronPickerState(state: CronPickerState): string {
  if (state.frequency === "custom") return state.expression;
  const { minute, hour, day, month, dayOfWeek } = fieldsOf(state);
  return [minute, hour, day, month, dayOfWeek].join(" ");
}

/**
 * Whether `value` is one a control of `field` displays exactly: a decimal
 * integer in range, written without a sign or a leading zero, since a number
 * input shows `05` as `5`.
 */
export function isControlValue(field: CronField, value: string): boolean {
  if (!/^(0|[1-9][0-9]*)$/.test(value)) return false;
  const n = Number(value);
  return n >= CONTROL_RANGES[field].min && n <= CONTROL_RANGES[field].max;
}

/** Every fixed frequency's state for these fields, most specific first. */
function candidatesFor(f: CronFields): readonly PickerStateOf<FixedFrequency>[] {
  return [
    { frequency: "yearly", minute: f.minute, hour: f.hour, day: f.day, month: f.month },
    { frequency: "monthly", minute: f.minute, hour: f.hour, day: f.day },
    { frequency: "weekly", minute: f.minute, hour: f.hour, dayOfWeek: f.dayOfWeek },
    { frequency: "daily", minute: f.minute, hour: f.hour },
    { frequency: "hourly", minute: f.minute }
  ];
}

/**
 * Whether a candidate state holds `fields` exactly: every field it fixes is a
 * control value, and every field it leaves out is the wildcard in `fields`.
 */
function holdsExactly(candidate: PickerStateOf<FixedFrequency>, fields: CronFields): boolean {
  const controlled = CONTROLLED_FIELDS[candidate.frequency];
  return CRON_FIELDS.every((field) =>
    controlled.includes(field) ? isControlValue(field, fields[field]) : fields[field] === ANY
  );
}

/** The picker state an expression opens as. Never throws; anything unclaimed is custom. */
export function parseCronPickerState(expression: string): CronPickerState {
  const custom: CronPickerState = { frequency: "custom", expression };
  // A single space, not `\s+`: an expression written with any other spacing is
  // not one the picker writes back, so claiming it would change the stored text.
  const [minute, hour, day, month, dayOfWeek, ...extra] = expression.split(" ");
  if (
    minute === undefined ||
    hour === undefined ||
    day === undefined ||
    month === undefined ||
    dayOfWeek === undefined ||
    extra.length > 0
  ) {
    return custom;
  }

  const fields: CronFields = { minute, hour, day, month, dayOfWeek };
  const claimed = candidatesFor(fields).find((candidate) => holdsExactly(candidate, fields));
  if (claimed === undefined) return custom;
  return serializeCronPickerState(claimed) === expression ? claimed : custom;
}
